import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { TableCellLayout, TableHeaderCell, mergeClasses } from '@fluentui/react-components';
import { type Header, flexRender } from '@tanstack/react-table';
import { px } from '@this/app-shared/dist/tools/styles.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { DragAndDropData } from '../../hooks/_shared/column-drag.js';
import { useResizeHandler } from '../../hooks/resize-handler.js';
import { ResizableTableCellBlank } from '../cell-blank/index.js';
import { useStyles } from './styles.js';

type HeaderCellProps<TData, TValue> = {
    defaultSize: number;
    header: Header<TData, TValue>;
    onResize: (size: number) => void;
    padContainerValue?: number | string;
    position?: 'first' | 'last';
    isLastColumn: boolean;
};

export function ResizableTableCellHeader<TData, TValue>({
    defaultSize,
    header,
    isLastColumn,
    onResize,
    position,
    padContainerValue,
}: HeaderCellProps<TData, TValue>) {
    const styles = useStyles();

    const [isHovered, setIsHovered] = useState(false);

    const { isDragging, resizeHandlerOffset, width, onResizeHandlerClick } = useResizeHandler({
        defaultSize,
        onResize,
    });

    useEffect(() => {
        const elements = document.querySelectorAll('[data-resizable-table-header-resize-handle-active]');

        elements.forEach((element) => {
            element.setAttribute('data-resizable-table-header-resize-handle-active', isDragging.toString());
        });
    }, [isDragging]);

    const draggableRef = useRef<HTMLDivElement | null>(null);
    const dropableRef = useRef<HTMLDivElement | null>(null);

    const dragAndDropData: DragAndDropData = useMemo(
        () => ({
            id: header.id,
        }),
        [header.id]
    );

    useEffect(() => {
        const element = draggableRef.current;

        if (!element) {
            return;
        }

        return draggable({
            element,
            getInitialData: (_) => dragAndDropData,
        });
    }, [dragAndDropData]);

    useEffect(() => {
        const element = dropableRef.current;

        if (!element) {
            return;
        }

        return dropTargetForElements({
            element: element,
            getData: (_) => dragAndDropData,
            canDrop: (data) => ((data.source.data as DragAndDropData).id === header.id ? false : true),
            onDragEnter: () => setIsHovered(true),
            onDragLeave: () => setIsHovered(false),
            onDrop: () => setIsHovered(false),
        });
    }, [dragAndDropData, header.id]);

    const resizable = header.column.getCanResize();

    return (
        <>
            {position === 'first' && <ResizableTableCellBlank size={padContainerValue ?? 0} />}

            <TableHeaderCell
                key={header.id}
                className={mergeClasses(styles.cellHeader, isHovered && styles.cellHeaderHovered)}
                colSpan={header.colSpan}
                style={{
                    width: px(width),
                    minWidth: px(width),
                    maxWidth: px(width),
                    paddingTop: padContainerValue,
                }}
            >
                <TableCellLayout
                    ref={(element) => {
                        draggableRef.current = element;
                        dropableRef.current = element;
                    }}
                    className={styles.cellLayout}
                >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </TableCellLayout>

                {
                    <div
                        className={mergeClasses(
                            styles.resizeHandlerContainer,
                            !resizable && styles.resizeHandlerContainerDisabled,
                            isLastColumn && styles.resizeHandlerContainerLastColumn
                        )}
                        onMouseDown={resizable ? onResizeHandlerClick : undefined}
                    >
                        <div
                            data-resizable-table-header-resize-handle
                            data-resizable-table-header-resize-handle-active={isDragging}
                            className={mergeClasses(
                                styles.resizeHandler,
                                !resizable && styles.resizeHandlerDisabled,
                                isDragging && styles.resizeHandlerDragging,
                                isLastColumn && styles.resizeHandlerDraggingLastColumn,
                                isLastColumn && isDragging && styles.resizeHandlerDraggingLastColumnDragging
                            )}
                            style={{
                                marginRight: px(resizeHandlerOffset),
                                cursor: resizable ? 'col-resize' : 'default',
                            }}
                        />
                    </div>
                }
            </TableHeaderCell>

            {position === 'last' && <ResizableTableCellBlank size={padContainerValue ?? 0} />}
        </>
    );
}
